import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import SalesDocDetails from "../../_components/Sales/SalesDocDetails";
import { FiCalendar, FiFileText, FiHash } from "react-icons/fi";
import { FieldInput, InfoRow, SimpleSelect } from "../../_components/Sales/SalesShared";
import {
    getDOById,
    updateDeliveryOrder,
    createDeliveryOrder,
    getSalesOrders,
    getInvoiceById,
} from "../../_services/sales.service";
import { getCustomers } from "../../_services/contact.service";
import { companyProfileService } from "../../_services/companyProfile.service";
import { isBusinessUnitManager, isSuperAdmin, canAccessAccounting } from '../../utils/roleConstants';

// --- Customer data fetchers ---
let customerCache = {};
async function getCachedCustomer(customerName) {
    if (customerCache[customerName]) return customerCache[customerName];
    const res = await getCustomers({ pageSize: 1000, page: 1, search: customerName, searchType: "name" });
    customerCache[customerName] = res?.data || [];
    return customerCache[customerName];
}

async function fetchCustomerNames() {
    const res = await getCustomers({ pageSize: 1000, page: 1 });
    const names = (res?.data || [])
        .filter(r => (r.status || r.Status || "").toLowerCase() === "active")
        .map(r => r.name)
        .filter(Boolean);
    return [...new Set(names)].sort();
}

async function fetchContactPersons(customerName) {
    if (!customerName) return [];
    const data = await getCachedCustomer(customerName);
    return data
        .filter(r => r.name === customerName && r.attention && (r.status || r.Status || "").toLowerCase() === "active")
        .map(r => r.attention)
        .filter((v, i, a) => a.indexOf(v) === i);
}

async function fetchCustomerDetails(customerName, addressField = "shippingAddress") {
    if (!customerName) return {};
    const data = await getCachedCustomer(customerName);
    const match = data.find((r) => r.name === customerName);
    if (!match) return {};
    return {
        paymentTerms: match.creditTerms || match.paymentTerms || null,
        customerAddress: match.billingAddress || match.address || "",
        shippingAddress: match.shippingAddress || "",
        phoneNumber: match.phoneNumber || match.phone_number || "",
    };
}


/* ══════════════════════════════════════════════════════════════
   DELIVERY ORDER DETAILS
══════════════════════════════════════════════════════════════ */
export default function DeliveryOrderDetails() {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // BUM can only view — hide create button
    const viewOnly = isBusinessUnitManager(user) && !canAccessAccounting(user) && !isSuperAdmin(user);
    const [salesOrders, setSalesOrders] = useState([]);
    const [selectedSO, setSelectedSO] = useState(null);
    const [initialDraft, setInitialDraft] = useState(null);
    const [searchParams] = useSearchParams();
    const fromInvoice = !!searchParams.get("fromInvoice");
    const invoiceRef = searchParams.get("invoiceRef");

    const fetchSalesOrders = useCallback((customerName, contactPerson) => {
        if (!customerName || !contactPerson) {
            setSalesOrders([]);
            setSelectedSO(null);
            return;
        }
        getSalesOrders({
            pageSize: 500,
            page: 1,
            status: "Approved",
            search: customerName,
            searchType: 'customer'
        })
            .then(r => {
                const customerSOs = r?.data || [];
                const contactSOs = customerSOs.filter(q => q.contactPerson === contactPerson);
                setSalesOrders(contactSOs);
            })
            .catch(() => setSalesOrders([]));
    }, []);

    const handleSOSelect = useCallback((soId) => {
        const found = salesOrders.find(q => q.so_id === soId || q.id === soId);
        setSelectedSO(found || null);
    }, [salesOrders]);

    const fillData = useMemo(() => {
        if (!selectedSO) return null;
        return {
            customer: selectedSO.customer || "",
            contactPerson: selectedSO.contactPerson || "",
            phoneNumber: selectedSO.phoneNumber || selectedSO.phone_number || "",
            customerAddress: selectedSO.customer_address || selectedSO.customerAddress || "",
            shippingAddress: selectedSO.shipping_address || selectedSO.shippingAddress || "",
            paymentTerms: selectedSO.payment_terms || selectedSO.paymentTerms || "",
            items: (selectedSO.items || []).map(item => ({ ...item, selected: true })),
            soRef: selectedSO.id || selectedSO.so_id,
            custPONo: selectedSO.cust_po_no || selectedSO.custPONo || "",
        };
    }, [selectedSO]);

    const fetchCustDetails = useCallback((name) => fetchCustomerDetails(name, "shippingAddress"), []);
    const fetchCompProfile = useCallback(() => companyProfileService.getProfile(), []);

    const doExtra = useMemo(() => ({
        left: ({ draft, onChange, errors, edit, dateMin, salesOrders: availableSOs }) => (
            edit ? (
                <>
                    <FieldInput
                        label="Delivery Date"
                        name="deliveryDate"
                        type="date"
                        value={draft?.deliveryDate || ""}
                        onChange={onChange}
                        min={draft?.date || dateMin}
                        required
                        error={errors?.deliveryDate}
                    />
                    <SimpleSelect
                        label="SO Reference No."
                        name="soRef"
                        value={draft?.soRef || ""}
                        onChange={(e) => {
                            onChange(e);
                            handleSOSelect(e.target.value);
                        }}
                        options={(availableSOs || []).map(q => q.id)}
                        placeholder="Select Sales Order (Approved)..."
                        required
                        error={errors?.so_ref}
                        disabled={!draft?.customer || !draft?.contactPerson || (availableSOs || []).length === 0}
                    />
                    {draft?.invoiceRef ? (
                        <FieldInput
                            label="Invoice Reference No."
                            name="invoiceRef"
                            value={draft.invoiceRef}
                            onChange={onChange}
                            readOnly={true}
                        />
                    ) : null}
                    <FieldInput
                        label="Customer PO No."
                        name="custPONo"
                        value={draft?.custPONo || ""}
                        onChange={onChange}
                        readOnly={true}
                    />
                </>
            ) : (
                <>
                    {draft?.deliveryDate && <InfoRow icon={FiCalendar} label="Delivery Date">{draft.deliveryDate}</InfoRow>}
                    {draft?.soRef || draft?.so_ref || draft?.so_id || draft?.soReference || draft?.quotationRef
                        ? <InfoRow icon={FiFileText} label="SO Reference No.">{draft.soRef || draft.so_ref || draft.so_id || draft.soReference || draft.quotationRef}</InfoRow>
                        : null
                    }
                    {draft?.invoiceRef
                        ? <InfoRow icon={FiFileText} label="Invoice Reference No.">{draft.invoiceRef}</InfoRow>
                        : null
                    }
                    {draft?.custPONo || draft?.cust_po_no
                        ? <InfoRow icon={FiHash} label="Cust PO No.">{draft.custPONo || draft.cust_po_no}</InfoRow>
                        : null
                    }
                </>
            )
        ),
        right: null,
    }), [handleSOSelect]);

    useEffect(() => {
        const fromSOId = searchParams.get("fromSO");
        if (!fromSOId) return;

        getSalesOrders({ pageSize: 500, page: 1 })
            .then(r => {
                const found = (r?.data || []).find(so => so.id === fromSOId);
                if (found) {
                    // Seed the SO dropdown list so it has something to select
                    setSalesOrders([found]);
                    // Trigger fillData (items + addresses) via existing handleSOSelect logic
                    setSelectedSO(found);
                    // Seed the header fields (customer, contactPerson, etc.)
                    setInitialDraft({
                        customer: found.customer || "",
                        contactPerson: found.contactPerson || "",
                        customerAddress: found.customerAddress || found.customer_address || "",
                        shippingAddress: found.shippingAddress || found.shipping_address || "",
                        paymentTerms: found.paymentTerms || found.payment_terms || "",
                        phoneNumber: found.phoneNumber || found.phone_number || "",
                        soRef: found.id,
                        custPONo: found.custPONo || found.cust_po_no || "",
                        invoiceRef: invoiceRef || undefined,
                    });
                }
            })
            .catch(() => { });
    }, []);

    return (
        <SalesDocDetails
            docType="Delivery Order"
            basePath="/sales/delivery-orders"
            fetchById={getDOById}
            updateItem={updateDeliveryOrder}
            createItem={createDeliveryOrder}
            fetchCustomerNames={fetchCustomerNames}
            fetchContactPersons={fetchContactPersons}
            fetchCustomerDetails={fetchCustDetails}
            fetchCompanyProfile={fetchCompProfile}
            taxRate={0}
            statuses={["Draft", "Pending for Approval", "Approved", "Rejected"]}
            emptyDraft={{ deliveryDate: "", soRef: "", custPONo: "", customerAddress: "", shippingAddress: "", invoiceRef: invoiceRef || "" }}
            extraFields={doExtra}
            addressLabel="Billing Address"
            addressField="customerAddress"
            fillDraft={fillData}
            lineItemProps={{
                selectable: false,
                noPrice: true,
                readOnly: true,
            }}
            onDependentFieldChange={fetchSalesOrders}
            extraFieldProps={{ salesOrders }}
            hidePaymentTerms={false}
            hideCurrency={true}
            initialDraft={initialDraft}
            skipApproval={fromInvoice}
            viewOnly={viewOnly}
        />
    );
}